import { numFont } from '../../../utils/fonts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { formatPriceWithCurrency } from '../../../utils/vat';

export function OemPackageCardRecommended({ pkg, picked, onPick }) {
  const { t } = useTranslation();
  const subtitle = subtitleOf(
    pkg,
    pkg.km ? t('oem.recommendedForKm', { km: formatKm(pkg.km) }) : pkg.interval_label
  );

  return (
    <Box
      onClick={onPick}
      sx={{
        border: 2,
        borderColor: picked ? 'secondary.main' : 'divider',
        borderRadius: '10px',
        p: '14px 16px',
        cursor: 'pointer',
        bgcolor: picked ? '#f5f9ff' : '#fff',
        transition: 'all 150ms',
        position: 'relative',
        '&:hover': { borderColor: 'secondary.main', bgcolor: '#f5f9ff' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          left: 14,
          bgcolor: 'secondary.main',
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          px: 1,
          py: 0.25,
          borderRadius: '4px',
        }}
      >
        {t('oem.recommended')}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: 2,
            borderColor: picked ? 'secondary.main' : '#cbd5e1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: picked ? 'secondary.main' : '#fff',
          }}
        >
          {picked && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {pkg.heading ?? (formatKmLabel(pkg.km) || pkg.title || pkg.interval_label)}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.125 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, ...numFont }}>
            ≈ {formatPriceWithCurrency(pkg.estimated_total || 0)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {pkg.service_time?.toFixed(1)}h · {pkg.part_count} {t('oem.parts')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function OemPackageCardSmall({ pkg, picked, onPick }) {
  const { t } = useTranslation();

  return (
    <Box
      onClick={onPick}
      sx={{
        border: 1.5,
        borderColor: picked ? 'secondary.main' : 'divider',
        borderRadius: '10px',
        p: '10px 12px',
        cursor: 'pointer',
        bgcolor: picked ? '#f5f9ff' : '#fff',
        transition: 'all 150ms',
        '&:hover': { borderColor: 'secondary.main', bgcolor: '#f5f9ff' },
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, ...numFont }}>
        {pkg.heading ?? (formatKmLabel(pkg.km) || pkg.title || pkg.interval_label)}
      </Typography>
      {subtitleOf(pkg, pkg.description) && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
          {subtitleOf(pkg, pkg.description)}
        </Typography>
      )}
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.75, ...numFont }}>
        {pkg.service_time?.toFixed(1)}h · {pkg.part_count} {t('oem.parts')}
      </Typography>
    </Box>
  );
}

function formatKm(km) {
  return Number(km).toLocaleString('fi-FI');
}

// The caller may hand over a ready subtitle (an empty string meaning "none");
// without one the card falls back to the legacy per-field wording.
function subtitleOf(pkg, fallback) {
  return pkg.subtitle !== undefined ? pkg.subtitle : fallback;
}

// Empty string for time-based and named schedules ("Oil Service", "Inspection I"),
// which carry no odometer figure — callers fall back to the service title.
function formatKmLabel(km) {
  return km ? `${formatKm(km)} km` : '';
}
